import React, { useState } from 'react';
import { format, isWeekend, isAfter, isBefore, isEqual } from 'date-fns';
import './DateRangePicker.scss';

const CustomDateRangePicker = ({ ranges, onChange }) => {
  const range = ranges[0];
  const [startDate, setStartDate] = useState(range.startDate);
  const [endDate, setEndDate] = useState(range.endDate);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectionState, setSelectionState] = useState('none'); // 'start', 'end', 'none'

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  
  // Create calendar grid
  const days = [];
  const monthStartDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Adjust for Monday as first day
  
  // Add empty cells for days before the 1st of the month
  for (let i = 0; i < monthStartDay; i++) {
    days.push({ day: null, date: null });
  }
  
  // Add all days in the month
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    days.push({ day: i, date });
  }
  
  // Function to handle date selection
  const handleDateSelection = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    if (!date || isWeekend(date) || isBefore(date, today)) return;
    
    if (selectionState === 'none' || selectionState === 'end') {
      // Start a new selection
      setStartDate(date);
      setEndDate(date);
      setSelectionState('start');
      onChange({
        selection: {
          startDate: date,
          endDate: date,
          key: 'selection'
        }
      });
    } else if (selectionState === 'start') {
      // Complete the selection
      if (isBefore(date, startDate)) {
        setStartDate(date);
        onChange({
          selection: {
            startDate: date,
            endDate,
            key: 'selection'
          }
        });
      } else {
        setEndDate(date);
        setSelectionState('end');
        onChange({
          selection: {
            startDate,
            endDate: date,
            key: 'selection'
          }
        });
      }
    }
  };
  
  // Handle month navigation
  const handlePrevMonth = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Prevent navigating to months before current month
    if (year < currentYear || (year === currentYear && month <= currentMonth)) {
      return;
    }
    
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  
  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };
  
  // Get CSS class for a date cell
  const getDateClass = (date) => {
    if (!date) return '';
    
    const classes = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate comparison
    
    if (isWeekend(date)) classes.push('weekend');
    if (isBefore(date, today)) classes.push('disabled');
    if (isEqual(date, today)) classes.push('today');
    
    if (startDate && endDate) {
      if (isEqual(date, startDate)) classes.push('range-start');
      if (isEqual(date, endDate)) classes.push('range-end');
      
      if (
        isAfter(date, startDate) && 
        isBefore(date, endDate)
      ) {
        classes.push('in-range');
      }
    }
    
    return classes.join(' ');
  };
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  return (
    <div className="custom-date-range-picker">
      <div className="calendar-header">
        <button 
          className="month-nav-btn" 
          onClick={handlePrevMonth}
          aria-label="Previous month"
        >
          &lt;
        </button>
        <div className="current-month-year">
          {monthNames[month]} {year}
        </div>
        <button 
          className="month-nav-btn" 
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          &gt;
        </button>
      </div>
      
      <div className="weekdays">
        <div>Mon</div>
        <div>Tue</div>
        <div>Wed</div>
        <div>Thu</div>
        <div>Fri</div>
        <div>Sat</div>
        <div>Sun</div>
      </div>
      
      <div className="calendar-grid">
        {days.map((day, index) => (
          <div 
            key={index}
            className={`date-cell ${getDateClass(day.date)}`}
            onClick={() => handleDateSelection(day.date)}
          >
            {day.day}
          </div>
        ))}
      </div>
      
      <div className="dates-display">
        <div className="date-display">
          <span className="date-label">Start:</span>
          <span className="date-value">{startDate ? format(startDate, 'PP') : 'Select'}</span>
        </div>
        <div className="date-display">
          <span className="date-label">End:</span>
          <span className="date-value">{endDate ? format(endDate, 'PP') : 'Select'}</span>
        </div>
      </div>
    </div>
  );
};

export default CustomDateRangePicker; 